import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../../shared/utils/currency';

// A punchier, more distinct palette than the muted one used elsewhere -
// this chart is meant to make individual holdings easy to tell apart.
const COLORS = [
    '#7c3aed', '#e74c3c', '#00b894', '#f39c12', '#0984e3',
    '#e84393', '#00cec9', '#fdcb6e', '#6c5ce7', '#d63031'
];

const CustomTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip card p-2">
        <p className="label mb-1">{`Month: ${label}`}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey} className="mb-0" style={{ color: entry.stroke }}>
            {`${entry.dataKey}: ${formatCurrency(entry.value, currency)}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// One line per subtype within the given asset type - easier to compare
// which individual holdings are growing/shrinking than the stacked bars
// in SubtypeDistributionChart, at the cost of not showing the total.
const PerHoldingTrendChart = ({ data, currency, assetType }) => {
  if (!data || data.length === 0 || !assetType) {
    return <p>No data available for the selected asset type.</p>;
  }

  const filteredData = data.filter(item => item.AssetType === assetType);
  if (filteredData.length === 0) {
    return <p>No data available for {assetType}.</p>;
  }

  const subtypes = Array.from(new Set(filteredData.map(item => item.AssetSubtype || 'N/A')));

  const byMonth = new Map();
  filteredData.forEach(({ YearMonth, AssetSubtype, total_value }) => {
    const key = AssetSubtype || 'N/A';
    if (!byMonth.has(YearMonth)) byMonth.set(YearMonth, { YearMonth });
    byMonth.get(YearMonth)[key] = (byMonth.get(YearMonth)[key] || 0) + total_value;
  });

  const chartData = Array.from(byMonth.values()).sort((a, b) => a.YearMonth.localeCompare(b.YearMonth));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <defs>
          {subtypes.map((key, index) => (
            <linearGradient key={key} id={`holdingGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.5} />
              <stop offset="100%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.03} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="YearMonth" />
        <YAxis tickFormatter={(value) => formatCurrency(value, currency)} width={90} />
        <Tooltip content={<CustomTooltip currency={currency} />} />
        <Legend />
        {subtypes.map((key, index) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke={COLORS[index % COLORS.length]}
            strokeWidth={2.5}
            fill={`url(#holdingGradient-${index})`}
            dot={{ r: 2, stroke: COLORS[index % COLORS.length], strokeWidth: 1, fill: '#fff' }}
            activeDot={{ r: 5 }}
            connectNulls
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default PerHoldingTrendChart;
